/* Qua cookie banner: one file for the claude.ai previews and the Shopify site (as assets/qua-consent.js).
   On Shopify it uses Shopify's own consent system (the Customer Privacy API): the banner shows only where the law asks
   for it (Shopify's region setting) and only until the visitor chooses, and Shopify's statistics and Google Analytics
   follow that choice. In a preview, where there is no Shopify, the choice is only remembered in this browser.
   Two equal buttons: declining is as easy as accepting. "Cookie settings" in the footer brings the banner back. */
(() => {
  const TEXT = {
    en: { line: "We use cookies for visitor statistics, only if you allow it. Never for advertising.", policy: "Privacy policy", yes: "Accept", no: "Decline", label: "Cookies" },
    nl: { line: "We gebruiken cookies voor bezoekersstatistieken, alleen als je dat toestaat. Nooit voor advertenties.", policy: "Privacybeleid", yes: "Accepteren", no: "Weigeren", label: "Cookies" }
  };
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;

  const css = document.createElement("style");
  css.textContent = `
  /* Shopify's own banner is replaced by this one */
  #shopify-pc__banner, #shopify-pc__prefs { display: none !important; }
  .qc { position: fixed; z-index: 60; left: 24px; bottom: calc(24px + env(safe-area-inset-bottom, 0px)); width: min(400px, calc(100% - 48px));
    display: grid; gap: 14px; padding: 18px; border-radius: 20px; background: var(--surface, #1C1C1E); color: var(--text2, #A1A1A6);
    border: 1px solid rgba(var(--rgb, 255, 255, 255), .09); box-shadow: 0 18px 50px rgba(0, 0, 0, calc(.5 * var(--sh, 1)));
    font: 400 .8125rem/1.55 var(--body, "Inter", system-ui, sans-serif); letter-spacing: normal;
    opacity: 0; transform: translateY(16px); transition: opacity 450ms var(--ease-out, ease), transform 450ms var(--ease-out, ease); }
  .qc.on { opacity: 1; transform: none; }
  .qc p { margin: 0; }
  .qc a { color: var(--fg, #FFFFFF); text-underline-offset: 3px; text-decoration-thickness: 1px; white-space: nowrap; }
  .qc .qc-btns { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
  .qc button { min-height: 40px; padding: 0 16px; border-radius: 999px; border: 0; cursor: pointer; background: rgba(var(--rgb, 255, 255, 255), .08);
    color: var(--fg, #FFFFFF); font: 500 .8125rem var(--body, "Inter", system-ui, sans-serif); letter-spacing: normal; transition: background-color 200ms ease, transform 160ms var(--ease-out, ease); }
  .qc button:active { transform: scale(.97); }
  .qc button:focus-visible { outline: 2px solid #4DD2E4; outline-offset: 2px; }
  @media (hover: hover) and (pointer: fine) { .qc button:hover { background: rgba(var(--rgb, 255, 255, 255), .14); } }
  /* phones: a slim strip with smaller text and buttons, so it covers little of the page (customer test 2026-10-09) */
  @media (max-width: 520px) {
    .qc { left: 10px; right: 10px; width: auto; bottom: calc(84px + env(safe-area-inset-bottom, 0px)); gap: 10px; padding: 12px 12px 12px 14px; border-radius: 16px; font-size: .75rem; line-height: 1.45; }
    .qc .qc-btns { gap: 6px; }
    .qc button { min-height: 36px; font-size: .75rem; }
  }
  @media (prefers-reduced-motion: reduce) { .qc { transition: none; } }`;
  document.head.appendChild(css);

  let api = null, box = null;
  const lang = () => /^nl\b/i.test(document.documentElement.lang || "") ? "nl" : "en";
  const privacyUrl = () => (document.querySelector('footer a[href*="privacy"]') || {}).getAttribute?.("href") || "/policies/privacy-policy";

  function show() {
    if (box) box.remove();
    const t = TEXT[lang()];
    box = document.createElement("div");
    box.className = "qc"; box.setAttribute("role", "region"); box.setAttribute("aria-label", t.label); box.lang = lang();
    box.innerHTML = `<p>${t.line} <a href="${privacyUrl()}">${t.policy}</a></p>
      <div class="qc-btns"><button type="button" data-yes>${t.yes}</button><button type="button" data-no>${t.no}</button></div>`;
    box.querySelector("[data-yes]").addEventListener("click", () => choose(true));
    box.querySelector("[data-no]").addEventListener("click", () => choose(false));
    document.body.prepend(box); // first in the keyboard order, still drawn at the bottom
    // room at the bottom of the page while the banner is up, so it never hides the footer links
    document.body.style.paddingBottom = box.offsetHeight + 24 + "px";
    requestAnimationFrame(() => requestAnimationFrame(() => box && box.classList.add("on")));
  }
  function hide() {
    if (!box) return;
    document.body.style.paddingBottom = "";
    const b = box; box = null; b.classList.remove("on");
    setTimeout(() => b.remove(), reduced ? 0 : 450);
  }
  function choose(yes) {
    // statistics only: Qua uses no marketing cookies and never sells data
    if (api) api.setTrackingConsent({ analytics: yes, marketing: false, preferences: yes, sale_of_data: false }, () => {});
    else try { localStorage.setItem("qua-consent", yes ? "accepted" : "declined"); } catch (e) {}
    hide();
  }

  // "Cookie settings" in the footer: bring the banner back to change the choice
  document.addEventListener("click", e => {
    const a = e.target.closest && e.target.closest("[data-cookie-settings]");
    if (!a) return;
    e.preventDefault(); show();
    const first = box && box.querySelector("button"); if (first) first.focus({ preventScroll: true });
  });

  function start() {
    if (window.Shopify && typeof Shopify.loadFeatures === "function") {
      Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], err => {
        if (err || !Shopify.customerPrivacy) return;
        api = Shopify.customerPrivacy;
        // shouldShowBanner() only says whether this region needs a banner; it stays true after a choice, so also check
        // that this visitor has not chosen yet (an empty answer means no choice)
        const chosen = (api.currentVisitorConsent() || {}).analytics;
        if (api.shouldShowBanner() && !chosen) setTimeout(show, reduced ? 0 : 900);
      });
      return;
    }
    let saved = null; try { saved = localStorage.getItem("qua-consent"); } catch (e) {}
    if (!saved) setTimeout(show, reduced ? 0 : 900);
  }
  if (document.readyState === "loading") addEventListener("DOMContentLoaded", start); else start();
})();